/**
 * A way into `?probe=1` on a television with no address bar.
 *
 * MEASURED ON A PANEL 2026-08-12. The Samsung WGT does not navigate to Foyer,
 * it TRANSCLUDES it (see hostQuery.ts): the document's URL is the widget's own
 * `file:///opt/usr/apps/...`, the bootloader hardcodes `/?tv=1`, and there is
 * no browser chrome to type into. So every diagnostic the probe prints was
 * unreachable from the one device we most needed to read it on.
 *
 * THE FIRST ATTEMPT PICKED A KEY THIS PANEL DOES NOT DELIVER. It used the red
 * colour key, and the bench had ALREADY reported "no color buttons nor 1234
 * works" - the digits and colour keys the bootloader claims are not arriving on
 * this remote. A shortcut built on an unproven key is not a shortcut; it is
 * another round trip to the bench.
 *
 * So the sequences below are built only from keys MEASURED as delivered on this
 * hardware: the arrows and Enter (standard W3C on this firmware) and Back
 * (10009, once tizenKeys.ts started translating it). The colour route is kept
 * as an alternate because it costs nothing and works on remotes that do send
 * them - but nothing depends on it any more.
 *
 * There is also a route that needs no remote at all: on Tizen the app logs its
 * platform inventory into the console ring buffer at boot, so "Capture logs" in
 * the CMS surfaces the installed app ids and the `webapis.systemcontrol` method
 * names without anyone standing in front of the television. See
 * tizenInventory.ts. That is the one to reach for first; this is for when the
 * panel is not talking to the fleet at all.
 */

import { hostSearch } from "./hostQuery";

/**
 * Sequences that open the probe. Any one of them fires it.
 *
 * The arrow sequence is deliberately six presses in a shape nobody produces by
 * accident - three would fire while a guest was simply moving around the
 * launcher grid. Arrows only move focus, so a partial run activates nothing.
 */
export const CHORD_SEQUENCES: readonly string[][] = [
  ["ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown", "ArrowLeft", "ArrowRight"],
  // Kept for remotes that do deliver the colour keys. Not relied upon.
  ["ColorF0Red", "ColorF0Red", "ColorF0Red"],
];

/** How long the whole sequence may take. */
export const CHORD_WINDOW_MS = 8000;

/** Every key that can begin or continue some sequence. */
const CHORD_KEYS = new Set(CHORD_SEQUENCES.flat());

let recent: Array<{ key: string; at: number }> = [];
let installed = false;
let onOpen: (() => void) | null = null;

/** Does the tail of `keys` match `seq` exactly? */
function tailMatches(keys: string[], seq: string[]): boolean {
  if (keys.length < seq.length) return false;
  const tail = keys.slice(keys.length - seq.length);
  return seq.every((k, i) => tail[i] === k);
}

function onKeyDown(e: KeyboardEvent): void {
  try {
    const key = e.key;
    if (!CHORD_KEYS.has(key)) {
      // A key that is in no sequence resets the run. A chord that tolerated
      // interruption would eventually fire during ordinary use, which is what
      // makes hidden diagnostics untrustworthy.
      recent = [];
      return;
    }
    const now = Date.now();
    recent = [...recent, { key, at: now }].filter(
      (p) => now - p.at <= CHORD_WINDOW_MS
    );
    const keys = recent.map((p) => p.key);
    if (!CHORD_SEQUENCES.some((seq) => tailMatches(keys, seq))) return;
    recent = [];
    onOpen?.();
  } catch {
    // A diagnostic shortcut must never be the reason a remote stops working.
  }
}

/**
 * Arm the chord. `open` is called when one completes - App renders the probe in
 * place rather than navigating, because navigating is exactly what a
 * transcluded widget cannot do.
 */
export function installProbeChord(open: () => void): void {
  onOpen = open;
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("keydown", onKeyDown);
}

/** Test seam: undo {@link installProbeChord}. */
export function uninstallProbeChord(): void {
  if (!installed) return;
  installed = false;
  recent = [];
  onOpen = null;
  window.removeEventListener("keydown", onKeyDown);
}

/** True when the query already asks for the probe (`?probe=1`). */
export function probeRequestedByQuery(): boolean {
  return new URLSearchParams(hostSearch()).get("probe") === "1";
}
